import React, { useMemo, useState } from 'react';
import {
  Plus,
  Trash2,
  ArrowDown,
  AlertTriangle,
  GitBranch,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import {
  FLOW_NODE_LABELS,
  FlowEdge,
  FlowNode,
  FlowNodeType,
  Template
} from '../../../types/models/whatsapp';

interface FlowEditorProps {
  nodes: FlowNode[];
  edges: FlowEdge[];
  templates: Template[];
  onChange: (nodes: FlowNode[], edges: FlowEdge[]) => void;
}

/** Node types offered in the palette — `trigger` is created automatically. */
const PALETTE: FlowNodeType[] = [
  'sendMessage',
  'askQuestion',
  'condition',
  'sendTemplate',
  'setAttribute',
  'addTag',
  'apiCall',
  'assignToAgent',
  'delay',
  'end'
];

const newId = (type: string) =>
  `${type}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * Linear flow editor.
 *
 * Deliberately a list rather than a drag-and-drop canvas: a working list
 * editor beats a half-finished graph canvas, and most support flows are a
 * sequence with the occasional branch. Condition nodes render their two
 * branches inline as sub-lists.
 */
const FlowEditor: React.FC<FlowEditorProps> = ({
  nodes,
  edges,
  templates,
  onChange
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const trigger = useMemo(() => nodes.find((n) => n.type === 'trigger'), [nodes]);

  /** Nodes in execution order, following the default (unbranched) edges. */
  const ordered = useMemo(() => {
    if (!trigger) return [];

    const result: FlowNode[] = [];
    const seen = new Set<string>();
    let currentId: string | undefined = trigger.id;

    while (currentId && !seen.has(currentId)) {
      // Bound the closures below to a per-iteration constant: `currentId` is
      // reassigned each pass, so capturing it directly is a stale-reference bug
      // waiting to happen (and eslint's no-loop-func flags it).
      const nodeId = currentId;
      seen.add(nodeId);

      const node = nodes.find((n) => n.id === nodeId);
      if (!node) break;
      result.push(node);

      const edge = edges.find((e) => e.from === nodeId && e.branch === undefined);
      currentId = edge?.to;
    }

    return result;
  }, [nodes, edges, trigger]);

  /** Nodes not on the main chain — branch targets and orphans. */
  const offChain = useMemo(
    () => nodes.filter((n) => !ordered.some((o) => o.id === n.id)),
    [nodes, ordered]
  );

  const updateNode = (id: string, patch: Partial<FlowNode['config']>) =>
    onChange(
      nodes.map((n) =>
        n.id === id ? { ...n, config: { ...n.config, ...patch } } : n
      ),
      edges
    );

  const appendNode = (type: FlowNodeType) => {
    const node: FlowNode = { id: newId(type), type, config: {} };

    if (type === 'condition') node.config = { operator: 'equals' };
    if (type === 'delay') node.config = { seconds: 60 };
    if (type === 'apiCall') node.config = { method: 'POST' };

    // Link from the last node on the chain, so appending Just Works.
    const last = ordered[ordered.length - 1];
    const nextEdges = last
      ? [...edges, { from: last.id, to: node.id }]
      : [...edges];

    onChange([...nodes, node], nextEdges);
    setSelectedId(node.id);
  };

  const removeNode = (id: string) => {
    // Re-link neighbours so deleting a middle step does not break the chain.
    const incoming = edges.find((e) => e.to === id && e.branch === undefined);
    const outgoing = edges.find((e) => e.from === id && e.branch === undefined);

    let nextEdges = edges.filter((e) => e.from !== id && e.to !== id);
    if (incoming && outgoing) {
      nextEdges = [...nextEdges, { from: incoming.from, to: outgoing.to }];
    }

    onChange(
      nodes.filter((n) => n.id !== id),
      nextEdges
    );
    if (selectedId === id) setSelectedId(null);
  };

  /** Moves a node up or down the main chain by rewriting its edges. */
  const moveNode = (id: string, direction: -1 | 1) => {
    const index = ordered.findIndex((n) => n.id === id);
    const swapIndex = index + direction;
    // Index 0 is the trigger and never moves.
    if (index < 1 || swapIndex < 1 || swapIndex >= ordered.length) return;

    const reordered = [...ordered];
    [reordered[index], reordered[swapIndex]] = [
      reordered[swapIndex],
      reordered[index]
    ];

    const branchEdges = edges.filter((e) => e.branch !== undefined);
    const chainEdges = reordered.slice(0, -1).map((n, i) => ({
      from: n.id,
      to: reordered[i + 1].id
    }));

    onChange(nodes, [...chainEdges, ...branchEdges]);
  };

  const setBranch = (nodeId: string, branch: 'true' | 'false', targetId: string) => {
    const others = edges.filter(
      (e) => !(e.from === nodeId && e.branch === branch)
    );
    onChange(
      nodes,
      targetId ? [...others, { from: nodeId, to: targetId, branch }] : others
    );
  };

  const branchTarget = (nodeId: string, branch: string) =>
    edges.find((e) => e.from === nodeId && e.branch === branch)?.to || '';

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
      {/* ---- Chain ---- */}
      <div className="space-y-2">
        {!trigger && (
          <p className="rounded-lg bg-warning-50 p-3 text-xs text-warning-700">
            This flow has no trigger. Add one to decide when it runs.
          </p>
        )}

        {ordered.map((node, index) => (
          <div key={node.id}>
            {index > 0 && (
              <div className="flex justify-center py-0.5">
                <ArrowDown className="h-3 w-3 text-wa-muted" />
              </div>
            )}

            <button
              type="button"
              onClick={() => setSelectedId(node.id === selectedId ? null : node.id)}
              className={`flex w-full items-start gap-2 rounded-lg border p-2.5 text-left transition-colors ${
                node.id === selectedId
                  ? 'border-wa-green bg-wa-surface2'
                  : 'border-wa-border bg-wa-surface hover:border-wa-muted'
              }`}
            >
              <span className="mt-0.5 shrink-0 rounded bg-wa-surface2 px-1.5 py-0.5 text-[10px] font-medium uppercase text-wa-muted">
                {index === 0 ? 'When' : index}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-medium text-wa-text">
                  {FLOW_NODE_LABELS[node.type]}
                </span>
                <span className="block truncate text-[11px] text-wa-muted">
                  {summarize(node, templates)}
                </span>
              </span>

              {index > 0 && (
                <span className="flex shrink-0 flex-col">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      moveNode(node.id, -1);
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && moveNode(node.id, -1)}
                    className="cursor-pointer rounded p-0.5 text-wa-muted hover:text-wa-text"
                    aria-label="Move up"
                  >
                    <ChevronUp className="h-3 w-3" />
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      moveNode(node.id, 1);
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && moveNode(node.id, 1)}
                    className="cursor-pointer rounded p-0.5 text-wa-muted hover:text-wa-text"
                    aria-label="Move down"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </span>
                </span>
              )}

              {node.type !== 'trigger' && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeNode(node.id);
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && removeNode(node.id)}
                  className="shrink-0 cursor-pointer rounded p-1 text-wa-muted hover:text-danger-500"
                  aria-label={`Remove ${FLOW_NODE_LABELS[node.type]}`}
                >
                  <Trash2 className="h-3 w-3" />
                </span>
              )}
            </button>

            {node.id === selectedId && (
              <div className="mt-1.5 rounded-lg border border-wa-border bg-wa-bg p-3">
                <NodeSettings
                  node={node}
                  nodes={nodes}
                  templates={templates}
                  onChange={(patch) => updateNode(node.id, patch)}
                  branchTarget={branchTarget}
                  onSetBranch={setBranch}
                />
              </div>
            )}
          </div>
        ))}

        {offChain.length > 0 && (
          <div className="mt-4 rounded-lg border border-dashed border-wa-border p-3">
            <p className="flex items-center gap-1.5 text-[11px] font-medium text-wa-muted">
              <GitBranch className="h-3 w-3" />
              Branch targets and unlinked steps
            </p>
            <div className="mt-2 space-y-1.5">
              {offChain.map((node) => (
                <div key={node.id}>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedId(node.id === selectedId ? null : node.id)
                    }
                    className={`flex w-full items-center gap-2 rounded border p-2 text-left ${
                      node.id === selectedId
                        ? 'border-wa-green bg-wa-surface2'
                        : 'border-wa-border bg-wa-surface'
                    }`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs text-wa-text">
                        {FLOW_NODE_LABELS[node.type]}
                      </span>
                      <span className="block truncate text-[11px] text-wa-muted">
                        {summarize(node, templates)}
                      </span>
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeNode(node.id);
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && removeNode(node.id)}
                      className="cursor-pointer rounded p-1 text-wa-muted hover:text-danger-500"
                      aria-label="Remove"
                    >
                      <Trash2 className="h-3 w-3" />
                    </span>
                  </button>

                  {node.id === selectedId && (
                    <div className="mt-1.5 rounded-lg border border-wa-border bg-wa-bg p-3">
                      <NodeSettings
                        node={node}
                        nodes={nodes}
                        templates={templates}
                        onChange={(patch) => updateNode(node.id, patch)}
                        branchTarget={branchTarget}
                        onSetBranch={setBranch}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ---- Palette ---- */}
      <div className="space-y-1.5 self-start rounded-lg border border-wa-border bg-wa-surface p-3">
        <p className="text-xs font-medium text-wa-text">Add a step</p>
        {PALETTE.map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => appendNode(type)}
            className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-xs text-wa-muted transition-colors hover:bg-wa-surface2 hover:text-wa-text"
          >
            <Plus className="h-3 w-3 shrink-0" />
            {FLOW_NODE_LABELS[type]}
          </button>
        ))}
      </div>
    </div>
  );
};

/** One-line description of a node, for the chain list. */
const summarize = (node: FlowNode, templates: Template[]): string => {
  const c = node.config || {};

  switch (node.type) {
    case 'trigger':
      if (c.triggerType === 'keyword') {
        return c.keywords?.length
          ? `Contact says: ${c.keywords.join(', ')}`
          : 'No keywords set';
      }
      if (c.triggerType === 'any_message') return 'Any inbound message';
      if (c.triggerType === 'button_click') return 'Contact taps a button';
      if (c.triggerType === 'new_contact') return 'A new contact messages';
      return 'Trigger not configured';

    case 'sendMessage':
    case 'askQuestion':
      return c.text ? c.text.slice(0, 80) : 'No text yet';

    case 'sendTemplate': {
      const t = templates.find((x) => x.name === c.templateName);
      return t ? `${t.name} (${t.language})` : c.templateName || 'No template';
    }

    case 'condition':
      return c.field
        ? `${c.field} ${c.operator || 'equals'} ${c.value ?? ''}`
        : 'No condition set';

    case 'setAttribute':
      return c.attribute ? `${c.attribute} = ${c.value ?? ''}` : 'No attribute';

    case 'addTag':
      return c.tag || 'No tag';

    case 'apiCall':
      return c.url || 'No URL';

    case 'assignToAgent':
      return c.agentName || 'Any available agent — bot stops here';

    case 'delay':
      return `${c.seconds || 60} seconds`;

    case 'end':
      return 'Flow finishes';

    default:
      return '';
  }
};

interface NodeSettingsProps {
  node: FlowNode;
  nodes: FlowNode[];
  templates: Template[];
  onChange: (patch: Partial<FlowNode['config']>) => void;
  branchTarget: (nodeId: string, branch: string) => string;
  onSetBranch: (nodeId: string, branch: 'true' | 'false', targetId: string) => void;
}

const inputClass =
  'mt-1 w-full rounded-lg border-wa-border bg-wa-surface2 text-xs text-wa-text focus:border-wa-green focus:ring-wa-green';

const NodeSettings: React.FC<NodeSettingsProps> = ({
  node,
  nodes,
  templates,
  onChange,
  branchTarget,
  onSetBranch
}) => {
  const c = node.config || {};
  const others = nodes.filter((n) => n.id !== node.id);

  return (
    <div className="space-y-2">
      {node.type === 'trigger' && (
        <>
          <label className="block">
            <span className="text-[11px] font-medium text-wa-text">Run when</span>
            <select
              value={c.triggerType || 'keyword'}
              onChange={(e) => onChange({ triggerType: e.target.value as any })}
              className={inputClass}
            >
              <option value="keyword">The contact sends a keyword</option>
              <option value="any_message">Any inbound message</option>
              <option value="button_click">The contact taps a button</option>
              <option value="new_contact">A new contact messages first</option>
            </select>
          </label>

          {(c.triggerType || 'keyword') === 'keyword' && (
            <>
              <label className="block">
                <span className="text-[11px] font-medium text-wa-text">
                  Keywords (comma separated)
                </span>
                <input
                  type="text"
                  value={(c.keywords || []).join(', ')}
                  onChange={(e) =>
                    onChange({
                      keywords: e.target.value
                        .split(',')
                        .map((k) => k.trim())
                        .filter(Boolean)
                    })
                  }
                  placeholder="hi, hello, start"
                  className={inputClass}
                />
              </label>

              <label className="block">
                <span className="text-[11px] font-medium text-wa-text">
                  Matching
                </span>
                <select
                  value={c.matchType || 'exact'}
                  onChange={(e) => onChange({ matchType: e.target.value as any })}
                  className={inputClass}
                >
                  <option value="exact">Whole message matches exactly</option>
                  <option value="contains">Message contains the keyword</option>
                </select>
                <span className="mt-0.5 block text-[10px] text-wa-muted">
                  Exact is safer — "contains" fires on "I'll stop by later".
                </span>
              </label>
            </>
          )}
        </>
      )}

      {(node.type === 'sendMessage' || node.type === 'askQuestion') && (
        <>
          <label className="block">
            <span className="text-[11px] font-medium text-wa-text">Message</span>
            <textarea
              rows={3}
              value={c.text || ''}
              onChange={(e) => onChange({ text: e.target.value })}
              placeholder="Hi {{contact.name}}, how can we help?"
              className={`${inputClass} resize-none`}
            />
            <span className="mt-0.5 block text-[10px] text-wa-muted">
              {'Use {{contact.name}}, {{contact.phone}}, or any variable you saved.'}
            </span>
          </label>

          {node.type === 'askQuestion' && (
            <label className="block">
              <span className="text-[11px] font-medium text-wa-text">
                Save the reply as
              </span>
              <input
                type="text"
                value={c.variable || ''}
                onChange={(e) =>
                  onChange({ variable: e.target.value.replace(/[^\w]/g, '') })
                }
                placeholder="sport"
                className={`${inputClass} font-mono`}
              />
              <span className="mt-0.5 block text-[10px] text-wa-muted">
                {'Then use it later as {{sport}}.'}
              </span>
            </label>
          )}
        </>
      )}

      {node.type === 'sendTemplate' && (
        <label className="block">
          <span className="text-[11px] font-medium text-wa-text">Template</span>
          <select
            value={c.templateName || ''}
            onChange={(e) => {
              const t = templates.find((x) => x.name === e.target.value);
              onChange({
                templateName: e.target.value,
                templateLanguage: t?.language || 'en'
              });
            }}
            className={inputClass}
          >
            <option value="">Choose an approved template…</option>
            {templates.map((t) => (
              <option key={t.id} value={t.name}>
                {t.name} ({t.language})
              </option>
            ))}
          </select>
          {templates.length === 0 && (
            <span className="mt-0.5 block text-[10px] text-warning-700">
              No approved templates yet — create one under Templates.
            </span>
          )}
        </label>
      )}

      {node.type === 'condition' && (
        <>
          <div className="grid gap-2 sm:grid-cols-3">
            <label className="block">
              <span className="text-[11px] font-medium text-wa-text">Field</span>
              <input
                type="text"
                value={c.field || ''}
                onChange={(e) => onChange({ field: e.target.value })}
                placeholder="sport"
                className={`${inputClass} font-mono`}
              />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-wa-text">Is</span>
              <select
                value={c.operator || 'equals'}
                onChange={(e) => onChange({ operator: e.target.value as any })}
                className={inputClass}
              >
                <option value="equals">equal to</option>
                <option value="not_equals">not equal to</option>
                <option value="contains">containing</option>
                <option value="not_contains">not containing</option>
                <option value="is_empty">empty</option>
                <option value="is_not_empty">not empty</option>
                <option value="greater_than">greater than</option>
                <option value="less_than">less than</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-wa-text">Value</span>
              <input
                type="text"
                value={c.value || ''}
                onChange={(e) => onChange({ value: e.target.value })}
                className={inputClass}
              />
            </label>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            {(['true', 'false'] as const).map((branch) => (
              <label key={branch} className="block">
                <span className="text-[11px] font-medium text-wa-text">
                  If {branch === 'true' ? 'yes' : 'no'}, go to
                </span>
                <select
                  value={branchTarget(node.id, branch)}
                  onChange={(e) => onSetBranch(node.id, branch, e.target.value)}
                  className={inputClass}
                >
                  <option value="">Continue to the next step</option>
                  {others.map((n) => (
                    <option key={n.id} value={n.id}>
                      {FLOW_NODE_LABELS[n.type]} — {summarize(n, templates).slice(0, 30)}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        </>
      )}

      {node.type === 'setAttribute' && (
        <div className="grid gap-2 sm:grid-cols-2">
          <label className="block">
            <span className="text-[11px] font-medium text-wa-text">Attribute</span>
            <input
              type="text"
              value={c.attribute || ''}
              onChange={(e) => onChange({ attribute: e.target.value })}
              placeholder="city"
              className={`${inputClass} font-mono`}
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium text-wa-text">Value</span>
            <input
              type="text"
              value={c.value || ''}
              onChange={(e) => onChange({ value: e.target.value })}
              placeholder="{{answer}}"
              className={inputClass}
            />
          </label>
        </div>
      )}

      {node.type === 'addTag' && (
        <label className="block">
          <span className="text-[11px] font-medium text-wa-text">Tag</span>
          <input
            type="text"
            value={c.tag || ''}
            onChange={(e) => onChange({ tag: e.target.value })}
            placeholder="football-trial"
            className={inputClass}
          />
        </label>
      )}

      {node.type === 'apiCall' && (
        <>
          <div className="grid gap-2 sm:grid-cols-[6rem_1fr]">
            <label className="block">
              <span className="text-[11px] font-medium text-wa-text">Method</span>
              <select
                value={c.method || 'POST'}
                onChange={(e) => onChange({ method: e.target.value as any })}
                className={inputClass}
              >
                <option>POST</option>
                <option>GET</option>
                <option>PUT</option>
                <option>PATCH</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-wa-text">URL</span>
              <input
                type="url"
                value={c.url || ''}
                onChange={(e) => onChange({ url: e.target.value })}
                placeholder="https://api.example.com/hook"
                className={inputClass}
              />
            </label>
          </div>

          {c.method !== 'GET' && (
            <label className="block">
              <span className="text-[11px] font-medium text-wa-text">
                Body (JSON)
              </span>
              <textarea
                rows={3}
                value={c.body || ''}
                onChange={(e) => onChange({ body: e.target.value })}
                placeholder={'{"phone": "{{contact.phone}}"}'}
                className={`${inputClass} resize-none font-mono`}
              />
            </label>
          )}

          <label className="block">
            <span className="text-[11px] font-medium text-wa-text">
              Save response as (optional)
            </span>
            <input
              type="text"
              value={c.saveAs || ''}
              onChange={(e) =>
                onChange({ saveAs: e.target.value.replace(/[^\w]/g, '') })
              }
              className={`${inputClass} font-mono`}
            />
          </label>
        </>
      )}

      {node.type === 'assignToAgent' && (
        <p className="flex items-start gap-1.5 rounded bg-warning-50 p-2 text-[11px] text-warning-700">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          This hands the conversation to a human and switches the bot off for
          that chat, so the flow ends here.
        </p>
      )}

      {node.type === 'delay' && (
        <label className="block">
          <span className="text-[11px] font-medium text-wa-text">
            Wait for (seconds)
          </span>
          <input
            type="number"
            min={1}
            max={86400}
            value={c.seconds ?? 60}
            onChange={(e) => onChange({ seconds: Number(e.target.value) })}
            className={inputClass}
          />
          <span className="mt-0.5 block text-[10px] text-wa-muted">
            Resumed by the scheduler, so it survives a restart. Max 24 hours.
          </span>
        </label>
      )}
    </div>
  );
};

export default FlowEditor;
